// Fallback de "Canais recentes" (channelViews) em localStorage.
//
// POR QUE EXISTE: os canais recentes moram no IndexedDB (local-db, store
// `channelViews`). Em TVs webOS/Tizen 2017+ a quota de IDB é apertada e o SO
// trata "temporary storage" como EVICTABLE — ao longo de semanas ela esgota e
// os `put` passam a REJEITAR. Resultado no device do dono: os canais recentes
// NUNCA aparecem (nem na Home nem na TV ao Vivo), porque a gravação falhava em
// silêncio E a leitura (`getRecentChannels`) estourava sem tolerância.
//
// É o MESMO landmine que o "Continuar assistindo" (positions) já resolveu em
// `watch-progress-fallback.ts` — só que channelViews tinha ficado pra trás, sem
// rede de segurança. Este módulo é o espelho fiel daquele padrão pra recentes.
//
// SOLUÇÃO: quando o IDB rejeita, espelhamos o recente aqui. localStorage:
//  - é SÍNCRONO → sobrevive ao teardown agressivo do player (o `destroy()`/
//    `suspend()` do zapping roda logo após o cleanup; o microtask do put do IDB
//    pode não completar, mas a escrita síncrona aqui já persistiu).
//  - tem quota SEPARADA do IDB (não é a mesma "temporary storage" evictável).
//  - é minúsculo (~5MB) → guardamos só os N registros MAIS RECENTES de canal
//    (LRU por lastWatchedAt), não o histórico inteiro.
//
// INVARIANTE: NUNCA lança. Qualquer erro de storage (indisponível, cheio, modo
// privado, file:// restrito) é engolido — o tracking do canal jamais quebra o
// player. O leitor de "Canais recentes" mescla esta fonte com o IDB.

import type { ChannelView } from './local-db'

const STORAGE_KEY = 'swipe:cv-fallback:v1'

/** Teto de registros no fallback. localStorage é pequeno; guardamos só os mais
 *  recentes (o "Canais recentes" lê até 50). Overshoot inofensivo. */
export const FALLBACK_MAX_ENTRIES = 50

/** Acesso defensivo ao localStorage — pode nem existir (SSR/teste node) ou
 *  lançar ao ser tocado (alguns WebViews em file://). */
function store(): Storage | null {
  try {
    if (typeof localStorage === 'undefined') return null
    return localStorage
  } catch {
    return null
  }
}

/** Type guard — records vindos do JSON podem estar corrompidos/parciais. */
function isChannelView(v: unknown): v is ChannelView {
  if (!v || typeof v !== 'object') return false
  const p = v as Record<string, unknown>
  return (
    typeof p['id'] === 'string' &&
    typeof p['providerScope'] === 'string' &&
    typeof p['channelId'] === 'string' &&
    typeof p['lastWatchedAt'] === 'number' &&
    typeof p['count'] === 'number'
  )
}

function readAll(): ChannelView[] {
  const s = store()
  if (!s) return []
  try {
    const raw = s.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(isChannelView)
  } catch {
    return []
  }
}

function writeAll(list: ChannelView[]): void {
  const s = store()
  if (!s) return
  try {
    // LRU: mantém só os mais recentes por lastWatchedAt.
    const bounded = [...list]
      .sort((a, b) => b.lastWatchedAt - a.lastWatchedAt)
      .slice(0, FALLBACK_MAX_ENTRIES)
    s.setItem(STORAGE_KEY, JSON.stringify(bounded))
  } catch {
    // localStorage cheio/indisponível — engole (nunca quebra o player).
  }
}

/** Grava/atualiza um registro de canal recente no fallback (upsert por id). */
export function fallbackUpsertChannelView(record: ChannelView): void {
  const list = readAll()
  const idx = list.findIndex((c) => c.id === record.id)
  if (idx >= 0) list[idx] = record
  else list.push(record)
  writeAll(list)
}

/** Todos os canais recentes do scope (provider+profile) atual. */
export function fallbackReadChannelViews(scope: string): ChannelView[] {
  return readAll().filter((c) => c.providerScope === scope)
}

/** Lookup de um registro por id scopado (`scope::groupKey` ou `scope::channelId`). */
export function fallbackReadChannelView(id: string): ChannelView | undefined {
  return readAll().find((c) => c.id === id)
}

/** Remove um registro (ex.: IDB voltou a funcionar e virou fonte canônica,
 *  ou o item foi deletado/limpo). No-op se não existir. */
export function fallbackRemoveChannelView(id: string): void {
  const list = readAll()
  const next = list.filter((c) => c.id !== id)
  if (next.length !== list.length) writeAll(next)
}

/** Limpa todos os registros de um scope EXATO (`p:CODE::pf:ID`). Usado por
 *  "Limpar histórico" pra o fallback não ressuscitar canais já apagados do IDB. */
export function fallbackClearChannelViewsScope(scope: string): void {
  const list = readAll()
  const next = list.filter((c) => c.providerScope !== scope)
  if (next.length !== list.length) writeAll(next)
}

/** Limpa os registros de TODOS os perfis de um provider (prefixo
 *  `${providerPart}::`). Espelha o corte por prefixo do IDB em
 *  `clearPersonalDataForProvider`. */
export function fallbackClearChannelViewsProviderPrefix(providerPart: string): void {
  const prefix = `${providerPart}::`
  const list = readAll()
  const next = list.filter((c) => !c.providerScope.startsWith(prefix))
  if (next.length !== list.length) writeAll(next)
}

/** Zera o bucket inteiro (factory reset / hard reset do local-db). */
export function fallbackClearChannelViewsAll(): void {
  const s = store()
  if (!s) return
  try {
    s.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}
